import React from "react";
import { styled } from "@mui/material/styles";
import ContentBody from "arena/components/ContentBody";
import ErrorScreen from "core/components/ErrorScreen";
import useScrollToTop from "core/hooks/useScrollToTop";
import { ContentQuery, CollectionTypesQuery } from "arena/queries";
import { useQuery } from "@apollo/client";
import analytics from "core/utils/analytics";
import { createEvent } from "core/utils/tracker";
import Grid from "@mui/material/Grid";
import { Navigate, useLocation, useParams } from "react-router-dom";
import NoMatchScreen from "arena/components/NoMatchScreen";
import ContentHelmet from "arena/components/ContentHelmet";
import { useArena } from "core/components/ArenaProvider";
import ArenaContents from "arena/components/ArenaContents";
import Box from "@mui/material/Box";
import NoSsr from "@mui/material/NoSsr";
import { useTranslation } from "react-i18next";

const Root = styled("div")(() => ({
  overflow: "hidden",
}));

const COLLECTION = "COLLECTION";

export default function Collection() {
  const location = useLocation();
  const { contentId } = useParams();
  const arena = useArena();
  const { t } = useTranslation("arena");

  useScrollToTop();

  const onCompleted = (data) => {
    if (data.content) {
      analytics.send({
        hitType: "pageview",
        page: location.pathname,
        title: data.content.title,
      });

      createEvent("page", "load");
    }
  };

  const { data, loading, error } = useQuery(ContentQuery, {
    // fetchPolicy: "cache-and-network",
    variables: { contentId },
    onCompleted,
  });

  const content = data?.content;

  const { data: collectionTypesData } = useQuery(CollectionTypesQuery, {
    fetchPolicy: "cache-and-network",
  });
  const collectionTypes = collectionTypesData?.collectionTypes || [];

  if (error) {
    return <ErrorScreen error={error} />;
  }

  if (!content && !loading) {
    return <NoMatchScreen />;
  }

  if (content && content.type !== COLLECTION) {
    return (
      <Navigate
        to={content.siteUrl.replace("collection", content.type.toLowerCase())}
      />
    );
  }

  const effectivePreviewMode =
    content?.collectionType?.collectionDisplayMode ??
    arena.defaultPreviewFormat ??
    "HORIZONTAL";

  return (
    <>
      <Root key={contentId}>
        <ContentHelmet content={content} arena={arena} />

        <Grid container spacing={4}>
          <Grid item xs={12}>
            <ContentBody content={content} loading={loading} noComments />
          </Grid>
          {content && (
            <Grid item xs={12}>
              <Box sx={{ px: { xs: 0, sm: 3 } }}>
                <NoSsr>
                  {collectionTypes.map((collectionType) => (
                    <ArenaContents
                      key={collectionType.id}
                      view="GRID"
                      sort={"LATEST"}
                      limit={12}
                      title={collectionType.title}
                      type={"COLLECTION"}
                      collectionId={contentId}
                      collectionTypeIds={[collectionType.id]}
                      previewMode={effectivePreviewMode}
                      noMore
                      loadMore
                      inView
                    />
                  ))}

                  <ArenaContents
                    view="GRID"
                    sort={"LATEST"}
                    limit={12}
                    title={t("contents.collections")}
                    type={"COLLECTION"}
                    collectionId={contentId}
                    collectionTypeIdsOnlyNull
                    previewMode={effectivePreviewMode}
                    noMore
                    loadMore
                    inView
                  />

                  <ArenaContents
                    view="GRID"
                    sort={"LATEST"}
                    limit={12}
                    title={t("contents.books")}
                    type={"BOOK"}
                    collectionId={contentId}
                    previewMode={effectivePreviewMode}
                    noMore
                    loadMore
                    inView
                  />

                  <ArenaContents
                    view="GRID"
                    sort={"LATEST"}
                    limit={12}
                    title={t("contents.shorts")}
                    type={"SHORTY"}
                    collectionId={contentId}
                    previewMode={effectivePreviewMode}
                    noMore
                    loadMore
                    inView
                  />

                  <ArenaContents
                    view="GRID"
                    sort={"LATEST"}
                    limit={12}
                    title={t("contents.videos")}
                    type={"VIDEO"}
                    collectionId={contentId}
                    previewMode={effectivePreviewMode}
                    noMore
                    loadMore
                    inView
                  />
                </NoSsr>
              </Box>
            </Grid>
          )}
        </Grid>
      </Root>
    </>
  );
}
